import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, CheckCircle2, Send } from 'lucide-react'
import { getTask, submitTask, type PortalSubmitDto, type NeueFirmaDto } from '@/api/zuzahlerTasks'
import Spinner from '@/components/Spinner'
import ErrorAlert from '@/components/ErrorAlert'

/**
 * Phase H — Detail einer KÜ-Aufgabe + Submit-Formular.
 *
 * Route: /zuzahler/tasks/:id
 * Auth:  eingeloggter Portal-User; C#-Service prueft Owner-Match.
 *
 * Inhalt:
 *  - Studi-Block (read-only)
 *  - Falls k.CrmAccountId leer (Case 4): Firmendaten-Block (Pflicht).
 *  - Rechnungsadresse + Anteil + Zahlungsart + Zahlungsweise
 *  - Submit → Inline-Erfolgsstate
 */
export default function TaskDetailPage() {
  const { id: idParam } = useParams<{ id: string }>()
  const id = Number(idParam)
  const navigate = useNavigate()

  if (!id || Number.isNaN(id)) {
    return (
      <div className="px-6 py-6 max-w-3xl mx-auto">
        <ErrorAlert message="Ungültige Aufgaben-ID." />
      </div>
    )
  }

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['zuzahlerTask', id],
    queryFn: () => getTask(id),
  })

  if (isLoading) return <Spinner className="mt-10" />
  if (error) {
    return (
      <div className="px-6 py-6 max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/zuzahler/tasks')}
          className="text-sm text-fom-600 hover:underline flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={14} /> Zur Übersicht
        </button>
        <ErrorAlert message={(error as Error).message} onRetry={refetch} />
      </div>
    )
  }
  if (!data) return null

  return <TaskDetailContent id={id} initial={data.item} />
}

interface KuMin {
  Id: number
  Status: string
  StudiAnrede?: string | null
  StudiVorname?: string | null
  StudiNachname?: string | null
  StudiMatrikel?: string | null
  StudiStudienort?: string | null
  StudiStudienbeginn?: string | null
  ZuzahlerFirma?: string | null
  ZuzahlerApName?: string | null
  ZuzahlerApEmail?: string | null
  CrmAccountId?: string | null
  AnteilProzent?: number | null
  Zahlungsart?: string | null
  Zahlungsweise?: string | null
  RechnungFirma?: string | null
  RechnungAdresszusatz?: string | null
  RechnungApName?: string | null
  RechnungEmailAp?: string | null
  RechnungEmailInvoice?: string | null
  RechnungAbteilung?: string | null
  RechnungTelefon?: string | null
  RechnungStrasse?: string | null
  RechnungHausnummer?: string | null
  RechnungPlz?: string | null
  RechnungOrt?: string | null
}

interface FormState {
  AnteilProzent: 100 | 50
  Zahlungsart: 'einmalig' | 'monatlich'
  Zahlungsweise: 'ueberweisung' | 'sepa'
  RechnungFirma: string
  RechnungAdresszusatz: string
  RechnungApName: string
  RechnungEmailAp: string
  RechnungEmailInvoice: string
  RechnungAbteilung: string
  RechnungTelefon: string
  RechnungStrasse: string
  RechnungHausnummer: string
  RechnungPlz: string
  RechnungOrt: string

  // Case 4 — komplett neue Firma
  NeueFirmaName: string
  NeueFirmaAddressLine1: string
  NeueFirmaPostalcode: string
  NeueFirmaCity: string
  NeueFirmaUstId: string
  NeueFirmaHrnummer: string
}

function TaskDetailContent({ id, initial }: { id: number; initial: KuMin }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const isCase4 = !initial.CrmAccountId

  const [form, setForm] = useState<FormState>(() => ({
    AnteilProzent: (initial.AnteilProzent === 50 ? 50 : 100) as 100 | 50,
    Zahlungsart: ((initial.Zahlungsart as 'einmalig' | 'monatlich') ?? 'einmalig'),
    Zahlungsweise: ((initial.Zahlungsweise as 'ueberweisung' | 'sepa') ?? 'ueberweisung'),
    RechnungFirma: initial.RechnungFirma ?? initial.ZuzahlerFirma ?? '',
    RechnungAdresszusatz: initial.RechnungAdresszusatz ?? '',
    RechnungApName: initial.RechnungApName ?? initial.ZuzahlerApName ?? '',
    RechnungEmailAp: initial.RechnungEmailAp ?? initial.ZuzahlerApEmail ?? '',
    RechnungEmailInvoice: initial.RechnungEmailInvoice ?? '',
    RechnungAbteilung: initial.RechnungAbteilung ?? '',
    RechnungTelefon: initial.RechnungTelefon ?? '',
    RechnungStrasse: initial.RechnungStrasse ?? '',
    RechnungHausnummer: initial.RechnungHausnummer ?? '',
    RechnungPlz: initial.RechnungPlz ?? '',
    RechnungOrt: initial.RechnungOrt ?? '',
    NeueFirmaName: initial.ZuzahlerFirma ?? '',
    NeueFirmaAddressLine1: '',
    NeueFirmaPostalcode: '',
    NeueFirmaCity: '',
    NeueFirmaUstId: '',
    NeueFirmaHrnummer: '',
  }))

  const [showErrors, setShowErrors] = useState(false)
  const [submitted, setSubmitted] = useState(initial.Status === 'signed')

  const errors = useMemo(() => validate(form, isCase4), [form, isCase4])

  const mut = useMutation({
    mutationFn: (dto: PortalSubmitDto) => submitTask(id, dto),
    onSuccess: () => {
      setSubmitted(true)
      qc.invalidateQueries({ queryKey: ['zuzahlerTasks'] })
      qc.invalidateQueries({ queryKey: ['zuzahlerTask', id] })
    },
  })

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (Object.keys(errors).length > 0) {
      setShowErrors(true)
      return
    }
    const dto: PortalSubmitDto = {
      AnteilProzent: form.AnteilProzent,
      Zahlungsart: form.Zahlungsart,
      Zahlungsweise: form.Zahlungsweise,
      RechnungFirma: form.RechnungFirma.trim(),
      RechnungAdresszusatz: emptyToUndef(form.RechnungAdresszusatz),
      RechnungApName: form.RechnungApName.trim(),
      RechnungEmailAp: form.RechnungEmailAp.trim(),
      RechnungEmailInvoice: emptyToUndef(form.RechnungEmailInvoice),
      RechnungAbteilung: emptyToUndef(form.RechnungAbteilung),
      RechnungTelefon: emptyToUndef(form.RechnungTelefon),
      RechnungStrasse: form.RechnungStrasse.trim(),
      RechnungHausnummer: form.RechnungHausnummer.trim(),
      RechnungPlz: form.RechnungPlz.trim(),
      RechnungOrt: form.RechnungOrt.trim(),
    }
    if (isCase4) {
      const neueFirma: NeueFirmaDto = {
        Name: form.NeueFirmaName.trim(),
        AddressLine1: form.NeueFirmaAddressLine1.trim(),
        Postalcode: form.NeueFirmaPostalcode.trim(),
        City: form.NeueFirmaCity.trim(),
        UstId: emptyToUndef(form.NeueFirmaUstId),
        Hrnummer: emptyToUndef(form.NeueFirmaHrnummer),
      }
      dto.NeueFirma = neueFirma
    }
    mut.mutate(dto)
  }

  const studiName = [initial.StudiAnrede, initial.StudiVorname, initial.StudiNachname]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="px-6 py-6 max-w-3xl mx-auto">
      <button
        onClick={() => navigate('/zuzahler/tasks')}
        className="text-sm text-fom-600 hover:underline flex items-center gap-1 mb-4"
      >
        <ArrowLeft size={14} /> Zur Übersicht
      </button>

      {submitted ? (
        <SuccessState />
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Studi-Block (read-only) */}
          <section className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="font-bold text-gray-900 text-lg mb-3">
              Kostenübernahme für
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <KV label="Name">{studiName || '–'}</KV>
              <KV label="Matrikel">
                <span className="font-mono">{initial.StudiMatrikel ?? '–'}</span>
              </KV>
              {initial.StudiStudienort && <KV label="Studienort">{initial.StudiStudienort}</KV>}
              {initial.StudiStudienbeginn && (
                <KV label="Studienbeginn">
                  {new Date(initial.StudiStudienbeginn).toLocaleDateString('de-DE')}
                </KV>
              )}
            </div>
          </section>

          {/* Case 4 — Firmendaten neu */}
          {isCase4 && (
            <section className="bg-white border border-amber-200 rounded-lg p-5">
              <h2 className="font-bold text-gray-900 text-lg mb-1">Firmendaten</h2>
              <p className="text-xs text-gray-500 mb-3">
                Ihre Firma ist bei uns noch nicht hinterlegt. Bitte tragen Sie die
                Daten einmalig ein — danach übernehmen wir sie automatisch ins CRM.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                  label="Firmenname *"
                  value={form.NeueFirmaName}
                  onChange={(v) => set('NeueFirmaName', v)}
                  invalid={showErrors && !!errors.NeueFirmaName}
                  className="sm:col-span-2"
                />
                <Field
                  label="Straße + Hausnr. *"
                  value={form.NeueFirmaAddressLine1}
                  onChange={(v) => set('NeueFirmaAddressLine1', v)}
                  invalid={showErrors && !!errors.NeueFirmaAddressLine1}
                  className="sm:col-span-2"
                />
                <Field
                  label="PLZ *"
                  value={form.NeueFirmaPostalcode}
                  onChange={(v) => set('NeueFirmaPostalcode', v)}
                  invalid={showErrors && !!errors.NeueFirmaPostalcode}
                />
                <Field
                  label="Ort *"
                  value={form.NeueFirmaCity}
                  onChange={(v) => set('NeueFirmaCity', v)}
                  invalid={showErrors && !!errors.NeueFirmaCity}
                />
                <Field
                  label="USt-ID (optional)"
                  value={form.NeueFirmaUstId}
                  onChange={(v) => set('NeueFirmaUstId', v)}
                />
                <Field
                  label="HR-Nummer (optional)"
                  value={form.NeueFirmaHrnummer}
                  onChange={(v) => set('NeueFirmaHrnummer', v)}
                />
              </div>
            </section>
          )}

          {/* Anteil */}
          <section className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="font-bold text-gray-900 text-lg mb-3">Kostenanteil</h2>
            <div className="flex gap-3">
              {[100, 50].map((p) => (
                <label
                  key={p}
                  className={`flex-1 cursor-pointer border rounded-lg p-3 text-center text-sm font-medium ${
                    form.AnteilProzent === p
                      ? 'border-fom-500 bg-fom-50 text-fom-700'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="anteil"
                    value={p}
                    checked={form.AnteilProzent === p}
                    onChange={() => set('AnteilProzent', p as 100 | 50)}
                    className="sr-only"
                  />
                  {p === 100 ? '100 % – komplette Übernahme' : '50 % – hälftige Übernahme'}
                </label>
              ))}
            </div>
          </section>

          {/* Zahlungsart + Zahlungsweise */}
          <section className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="font-bold text-gray-900 text-lg mb-3">Zahlung</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <RadioGroup
                label="Zahlungsart"
                value={form.Zahlungsart}
                options={[
                  { value: 'einmalig', label: 'Einmalig' },
                  { value: 'monatlich', label: 'Monatlich' },
                ]}
                onChange={(v) => set('Zahlungsart', v as 'einmalig' | 'monatlich')}
              />
              <RadioGroup
                label="Zahlungsweise"
                value={form.Zahlungsweise}
                options={[
                  { value: 'ueberweisung', label: 'Überweisung' },
                  { value: 'sepa', label: 'SEPA-Lastschrift' },
                ]}
                onChange={(v) => set('Zahlungsweise', v as 'ueberweisung' | 'sepa')}
              />
            </div>
          </section>

          {/* Rechnungsanschrift */}
          <section className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="font-bold text-gray-900 text-lg mb-3">Rechnungsanschrift</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label="Rechnungsadressat (Firma) *"
                value={form.RechnungFirma}
                onChange={(v) => set('RechnungFirma', v)}
                invalid={showErrors && !!errors.RechnungFirma}
                className="sm:col-span-2"
              />
              <Field
                label="Adresszusatz"
                value={form.RechnungAdresszusatz}
                onChange={(v) => set('RechnungAdresszusatz', v)}
                className="sm:col-span-2"
              />
              <Field
                label="Ansprechpartner *"
                value={form.RechnungApName}
                onChange={(v) => set('RechnungApName', v)}
                invalid={showErrors && !!errors.RechnungApName}
              />
              <Field
                label="E-Mail Ansprechpartner *"
                value={form.RechnungEmailAp}
                onChange={(v) => set('RechnungEmailAp', v)}
                invalid={showErrors && !!errors.RechnungEmailAp}
                type="email"
              />
              <Field
                label="E-Mail für Rechnungen"
                value={form.RechnungEmailInvoice}
                onChange={(v) => set('RechnungEmailInvoice', v)}
                type="email"
              />
              <Field
                label="Abteilung"
                value={form.RechnungAbteilung}
                onChange={(v) => set('RechnungAbteilung', v)}
              />
              <Field
                label="Telefon"
                value={form.RechnungTelefon}
                onChange={(v) => set('RechnungTelefon', v)}
              />
              <Field
                label="Straße *"
                value={form.RechnungStrasse}
                onChange={(v) => set('RechnungStrasse', v)}
                invalid={showErrors && !!errors.RechnungStrasse}
              />
              <Field
                label="Hausnummer *"
                value={form.RechnungHausnummer}
                onChange={(v) => set('RechnungHausnummer', v)}
                invalid={showErrors && !!errors.RechnungHausnummer}
              />
              <Field
                label="PLZ *"
                value={form.RechnungPlz}
                onChange={(v) => set('RechnungPlz', v)}
                invalid={showErrors && !!errors.RechnungPlz}
              />
              <Field
                label="Ort *"
                value={form.RechnungOrt}
                onChange={(v) => set('RechnungOrt', v)}
                invalid={showErrors && !!errors.RechnungOrt}
              />
            </div>
          </section>

          {mut.error && (
            <ErrorAlert message={(mut.error as Error).message} />
          )}

          {showErrors && Object.keys(errors).length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              Bitte füllen Sie die rot markierten Pflichtfelder aus.
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="submit"
              disabled={mut.isPending}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-fom-600 hover:bg-fom-700 disabled:bg-gray-300 text-white font-semibold text-sm transition"
            >
              {mut.isPending ? (
                'Wird gesendet …'
              ) : (
                <>
                  <Send size={16} />
                  Verbindlich signieren & absenden
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

function SuccessState() {
  return (
    <div className="bg-white border border-emerald-200 rounded-lg p-8 text-center">
      <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
        <CheckCircle2 className="text-emerald-600" size={32} />
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">Vielen Dank!</h2>
      <p className="text-sm text-gray-600 max-w-md mx-auto">
        Die Kostenübernahmeerklärung wurde signiert. Das PDF wurde an die
        Buchhaltung der FOM Hochschule übermittelt. Sie müssen nichts weiter tun.
      </p>
    </div>
  )
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-gray-900">{children}</div>
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  invalid,
  type = 'text',
  className = '',
}: {
  label: string
  value: string
  onChange: (v: string) => void
  invalid?: boolean
  type?: string
  className?: string
}) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs font-semibold text-gray-700 mb-1">{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fom-500 ${
          invalid ? 'border-red-400 bg-red-50' : 'border-gray-300'
        }`}
      />
    </label>
  )
}

function RadioGroup({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <div>
      <div className="text-xs font-semibold text-gray-700 mb-1">{label}</div>
      <div className="flex gap-2">
        {options.map((o) => (
          <label
            key={o.value}
            className={`flex-1 cursor-pointer border rounded-md p-2 text-center text-sm ${
              value === o.value
                ? 'border-fom-500 bg-fom-50 text-fom-700 font-medium'
                : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <input
              type="radio"
              value={o.value}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
              className="sr-only"
            />
            {o.label}
          </label>
        ))}
      </div>
    </div>
  )
}

function emptyToUndef(s: string | null | undefined): string | undefined {
  const t = (s ?? '').trim()
  return t === '' ? undefined : t
}

function validate(form: FormState, isCase4: boolean): Record<string, string> {
  const e: Record<string, string> = {}
  if (form.AnteilProzent !== 50 && form.AnteilProzent !== 100)
    e.AnteilProzent = 'Anteil muss 50 oder 100 sein.'
  if (!form.RechnungFirma.trim()) e.RechnungFirma = 'Pflicht.'
  if (!form.RechnungApName.trim()) e.RechnungApName = 'Pflicht.'
  if (!form.RechnungEmailAp.trim()) e.RechnungEmailAp = 'Pflicht.'
  if (!form.RechnungStrasse.trim()) e.RechnungStrasse = 'Pflicht.'
  if (!form.RechnungHausnummer.trim()) e.RechnungHausnummer = 'Pflicht.'
  if (!form.RechnungPlz.trim()) e.RechnungPlz = 'Pflicht.'
  if (!form.RechnungOrt.trim()) e.RechnungOrt = 'Pflicht.'
  if (isCase4) {
    if (!form.NeueFirmaName.trim()) e.NeueFirmaName = 'Pflicht.'
    if (!form.NeueFirmaAddressLine1.trim()) e.NeueFirmaAddressLine1 = 'Pflicht.'
    if (!form.NeueFirmaPostalcode.trim()) e.NeueFirmaPostalcode = 'Pflicht.'
    if (!form.NeueFirmaCity.trim()) e.NeueFirmaCity = 'Pflicht.'
  }
  return e
}
